.field {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-4);
  font-family: var(--font-family);
}

.label {
  font: var(--text-body-medium);
  color: var(--text-primary);
}

/* Visually hidden but still reachable for screen readers.
 * Standard sr-only pattern (WCAG-friendly, won't be picked up by layout). */
.labelVisuallyHidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.inputRow {
  display: flex;
  align-items: center;
  gap: var(--spacing-8);
  background: var(--bg-primary);
  border: 1px solid var(--stroke-neutral);
  border-radius: var(--radius-8);
  padding-inline: var(--spacing-12);
  transition:
    border-color var(--motion-duration-fast) ease,
    box-shadow var(--motion-duration-fast) ease;
}

.inputRow:focus-within {
  border-color: var(--brand);
  /* Subtle focus ring using the brand color at low alpha. Text-field focus
   * intentionally differs from Button/IconButton's 1px neutral shadow ring:
   * text fields conventionally integrate focus into the field border
   * (border color shift + low-alpha halo) rather than wear an outer ring.
   * This is the pattern used by macOS/Apple Human Interface, Material 3
   * text-field focus, and Radix / shadcn / Mantine text inputs. Keeping
   * the button-family and field-family rings distinct avoids making a
   * focused input look like a focused button. */
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--brand) 18%, transparent);
}

.leadingIcon {
  display: inline-flex;
  align-items: center;
  color: var(--icon-muted);
  flex-shrink: 0;
}

.input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  color: var(--text-primary);
  font: inherit;
  padding: 0;
}

.input::placeholder {
  color: var(--text-tertiary);
}

/* ─── Sizes ──────────────────────────────────────────────────────── */

/* xs — compact field for dense rows where the input needs to sit
 * inline with xs-size body text (notification reply slot, inline
 * chat composers, micro-forms). min-height 28px matches Button's
 * `s` size so an xs Input and an s Button form a height-aligned
 * row. Font drops to --text-size-xs to harmonize with adjacent
 * xs body text rather than standing a notch larger. */
.size-xs .inputRow {
  min-height: 28px;
  padding-inline: var(--spacing-8);
}
.size-xs .input {
  font-size: var(--text-size-xs);
  line-height: var(--text-lh-xs);
}
/* Scale the visible label down with the field so xs + visible label
 * doesn't read as "big label over a compact box". The hideLabel path
 * (reply slot, search, etc.) never surfaces this — but consumers who
 * pass an xs Input with a visible label (future dense forms) need
 * the label to shrink alongside the input to stay visually coherent. */
.size-xs .label {
  font-size: var(--text-size-xs);
  line-height: var(--text-lh-xs);
}

.size-sm .inputRow {
  min-height: 32px;
}
.size-sm .input {
  font-size: var(--text-size-s);
  line-height: var(--text-lh-s);
}

.size-md .inputRow {
  min-height: 40px;
}
.size-md .input {
  font-size: var(--text-size-m);
  line-height: var(--text-lh-m);
}

/* ─── States ─────────────────────────────────────────────────────── */

.invalid .inputRow {
  border-color: var(--text-negative);
}
.invalid .inputRow:focus-within {
  border-color: var(--text-negative);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--text-negative) 18%, transparent);
}

.disabled .inputRow {
  background: var(--bg-secondary);
  cursor: not-allowed;
}
.disabled .input {
  cursor: not-allowed;
  color: var(--text-tertiary);
}

.help {
  font-size: var(--text-size-xs);
  line-height: var(--text-lh-xs);
  color: var(--text-tertiary);
}

.error {
  font-size: var(--text-size-xs);
  line-height: var(--text-lh-xs);
  color: var(--text-negative);
}
